import { apiErrorSchema } from '@competa/shared';
import type { ZodType } from 'zod';

/** Basispfad der API aus Sicht des Browsers (zur Build-Zeit über `VITE_API_BASE` einstellbar). */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/$/, '');

/**
 * Baut die vollständige Adresse eines API-Pfads (für Bilder, Downloads und Links außerhalb von `fetch`).
 * @param path Pfad relativ zur API-Basis, z. B. `/images/3/thumb`.
 * @returns Adresse inklusive Basispfad.
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/** Fehler einer API-Anfrage mit dem maschinenlesbaren Code aus dem einheitlichen Fehlerformat. */
export class ApiRequestError extends Error {
  /**
   * @param code Fehlercode der API (z. B. `not_found`) oder `network_error`.
   * @param status HTTP-Statuscode, 0 bei Netzwerkfehlern.
   * @param message Kurzer Text der API.
   * @param fields Optional: Fehlercode je Eingabefeld (bei Validierungsfehlern).
   */
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Callback, der bei abgelaufener oder ungültiger Sitzung (`unauthorized`) aufgerufen wird. */
let unauthorizedHandler: (() => void) | null = null;

/**
 * Registriert den globalen Handler für abgelaufene Sitzungen (setzt der Auth-Anbieter).
 * @param handler Wird bei jeder Antwort mit dem Fehlercode `unauthorized` aufgerufen; `null` entfernt ihn.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Name des CSRF-Cookies (lesbar für JavaScript; Double-Submit für multipart-Uploads, PLAN.md T21). */
const CSRF_COOKIE = '__Host-csrf';

/**
 * Liest das CSRF-Token aus dem (nicht HttpOnly) Cookie.
 * @returns Token oder leer, wenn nicht angemeldet.
 */
export function readCsrfToken(): string {
  const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${CSRF_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : '';
}

/** Optionen einer API-Anfrage. */
interface RequestOptions<T> {
  /** JSON-Körper (wird serialisiert). */
  body?: unknown;
  /** Schema zur Validierung der Antwort; ohne Schema wird die Antwort verworfen (z. B. bei 204). */
  schema?: ZodType<T>;
  /** Formulardaten (Datei-Upload); wird mit dem CSRF-Token im Header gesendet, ohne eigenen Content-Type. */
  form?: FormData;
}

/**
 * Führt eine Anfrage an die API aus, übersetzt Fehlerantworten in `ApiRequestError` und
 * validiert Erfolgsantworten mit einem Zod-Schema.
 * @param method HTTP-Methode.
 * @param path Pfad relativ zur API-Basis, z. B. `/auth/login`.
 * @param options Körper und Antwortschema.
 * @returns Die validierte Antwort (bzw. `undefined` ohne Schema).
 * @throws ApiRequestError bei Netzwerkfehlern oder Fehlerantworten der API.
 */
export async function apiRequest<T = undefined>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        // Beim Upload setzt der Browser den multipart-Content-Type samt Begrenzung selbst.
        ...(options.form ? { 'X-CSRF-Token': readCsrfToken() } : {}),
      },
      body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    });
  } catch {
    throw new ApiRequestError('network_error', 0, 'Netzwerkfehler');
  }

  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    const error = parsed.success
      ? new ApiRequestError(
          parsed.data.error.code,
          response.status,
          parsed.data.error.message,
          parsed.data.error.fields,
        )
      : new ApiRequestError('unknown', response.status, 'Unbekannter Fehler');
    if (error.code === 'unauthorized') unauthorizedHandler?.();
    throw error;
  }
  return (options.schema ? options.schema.parse(body) : undefined) as T;
}

/**
 * GET-Anfrage mit validierter Antwort.
 * @param path Pfad relativ zur API-Basis, z. B. `/health`.
 * @param schema Schema, gegen das die Antwort geprüft wird.
 * @returns Die validierte Antwort.
 */
export function apiGet<T>(path: string, schema: ZodType<T>): Promise<T> {
  return apiRequest('GET', path, { schema });
}
